import { useRef } from 'react';

import Box from '@zenbusiness/zui/Box';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Paper from '@zenbusiness/zui/Paper';
import PropTypes from 'prop-types';
import { useReactToPrint } from 'react-to-print';

import FormInfoModal from '#client/components/FormInfoModal';
import { ActionInfoButtonsGrid } from '#client/components/Grid';
import AdditionalInfo from '#client/components/TransactionDetailModal/components/AdditionalInfo';
import DetailWrapper from '#client/components/TransactionDetailModal/components/DetailWrapper';
import FXDetail from '#client/components/TransactionDetailModal/components/FXDetail';
import MerchantDetail from '#client/components/TransactionDetailModal/components/MerchantDetail';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';

import AuthorizationDetail from './AuthorizationDetail';
import { ButtonStyled, PrintedBox } from './AuthorizationDetailModal.style';

export default function AuthorizationDetailModal({
  amount,
  createdAt,
  type,
  id,
  status,
  reason,
  cardId,
  paymentMethod,
  digitalWallet,
  cardPresent,
  recurring,
  verificationMethod,
  merchantName,
  merchantLocation,
  merchantCategory,
  currencyConversion,
  handleClose,
  summary
}) {
  const contentRef = useRef(null);
  const {
    sizes: { isDesktop }
  } = useBreakpoints();

  const handlePrint = useReactToPrint({
    content: () => contentRef.current
  });

  useModalDisplayTracker({
    elementName: 'transactions_details_modal',
    elementText: 'transactions_details_modal',
    appPageName: 'transactions_details_modal'
  });

  return (
    <FormInfoModal
      borderRadius={isDesktop ? '16px' : '0'}
      fullScreen={!isDesktop}
      handleClose={handleClose}
      title={summary}
    >
      <DialogContent sx={{ padding: 0 }}>
        <DetailWrapper>
          <PrintedBox ref={contentRef}>
            <Paper variant="outlined">
              <AuthorizationDetail
                amount={amount}
                cardId={cardId}
                createdAt={createdAt}
                id={id}
                reason={reason}
                status={status}
                type={type}
              />
              <MerchantDetail
                cardPresent={cardPresent}
                digitalWallet={digitalWallet}
                merchantCategory={merchantCategory}
                merchantLocation={merchantLocation}
                merchantName={merchantName}
                paymentMethod={paymentMethod}
                recurring={recurring}
                verificationMethod={verificationMethod}
              />
              <FXDetail currencyConversion={currencyConversion} />
            </Paper>
          </PrintedBox>
          <AdditionalInfo handlePrint={handlePrint} />
        </DetailWrapper>
      </DialogContent>
      {!isDesktop && (
        <ActionInfoButtonsGrid>
          <Box>
            <ButtonStyled
              color="primary"
              onClick={handleClose}
              size="large"
              variant="text"
            >
              Close
            </ButtonStyled>
          </Box>
        </ActionInfoButtonsGrid>
      )}
    </FormInfoModal>
  );
}

AuthorizationDetailModal.propTypes = {
  summary: PropTypes.string,
  amount: PropTypes.string,
  createdAt: PropTypes.string,
  type: PropTypes.string,
  id: PropTypes.string,
  status: PropTypes.string,
  reason: PropTypes.string,
  cardId: PropTypes.string,
  // Merchant Detail
  paymentMethod: PropTypes.string,
  digitalWallet: PropTypes.string,
  cardPresent: PropTypes.bool,
  recurring: PropTypes.bool,
  verificationMethod: PropTypes.string,
  merchantName: PropTypes.string,
  merchantLocation: PropTypes.string,
  merchantCategory: PropTypes.string,
  // FX Details
  currencyConversion: PropTypes.shape({
    originalCurrency: PropTypes.string,
    amountInOriginalCurrency: PropTypes.number,
    fxRate: PropTypes.string
  }),
  handleClose: PropTypes.func
};
